/**
 * Credential encryption for AppFactory user profile storage.
 * Uses AES-GCM. Key from VITE_APPFACTORY_CREDENTIAL_ENCRYPTION_KEY (64 hex chars).
 * If not set, returns plain text (Firebase encrypts at rest).
 */

const ENCRYPTION_KEY_HEX = (import.meta.env.VITE_APPFACTORY_CREDENTIAL_ENCRYPTION_KEY as string) || ''

async function getKey(): Promise<CryptoKey | null> {
  if (!ENCRYPTION_KEY_HEX || ENCRYPTION_KEY_HEX.length !== 64 || !/^[0-9a-fA-F]+$/.test(ENCRYPTION_KEY_HEX)) {
    return null
  }
  const keyBytes = new Uint8Array(32)
  for (let i = 0; i < 32; i++) {
    keyBytes[i] = parseInt(ENCRYPTION_KEY_HEX.slice(i * 2, i * 2 + 2), 16)
  }
  return await crypto.subtle.importKey(
    'raw',
    keyBytes,
    { name: 'AES-GCM', length: 256 },
    false,
    ['encrypt', 'decrypt']
  )
}

export async function encryptCredential(plain: string): Promise<string> {
  const key = await getKey()
  if (!key) return plain
  const iv = crypto.getRandomValues(new Uint8Array(12))
  const encoded = new TextEncoder().encode(plain)
  const cipher = await crypto.subtle.encrypt(
    { name: 'AES-GCM', iv, tagLength: 128 },
    key,
    encoded
  )
  const combined = new Uint8Array(iv.length + cipher.byteLength)
  combined.set(iv)
  combined.set(new Uint8Array(cipher), iv.length)
  let binary = ''
  for (let i = 0; i < combined.length; i++) binary += String.fromCharCode(combined[i])
  return btoa(binary)
}

export async function decryptCredential(encrypted: string): Promise<string> {
  const key = await getKey()
  if (!key) return encrypted
  try {
    const combined = Uint8Array.from(atob(encrypted), (c) => c.charCodeAt(0))
    const iv = combined.slice(0, 12)
    const cipher = combined.slice(12)
    const dec = await crypto.subtle.decrypt(
      { name: 'AES-GCM', iv, tagLength: 128 },
      key,
      cipher
    )
    return new TextDecoder().decode(dec)
  } catch {
    return encrypted
  }
}

export function isEncryptionEnabled(): boolean {
  return Boolean(ENCRYPTION_KEY_HEX && ENCRYPTION_KEY_HEX.length === 64)
}
